import type { WorkspaceEntry } from '@crowdex/workspace-protocol';

/** Folders never listed: what tools build or fetch, and the repository's own. */
const SKIPPED = new Set([
  '.git',
  'node_modules',
  'dist',
  'build',
  'out',
  'coverage',
  '.next',
  '.turbo',
  '.cache',
  'target',
]);

/**
 * The files of a workspace, as the Files panel lists them (`WorkspaceClient.listDirectory`),
 * walked breadth first so the shallow ones come first; at most `maxFiles`, and `maxDepth`
 * folders deep. What search & commands (D21) finds by path.
 */
export async function listWorkspaceFiles(
  listDirectory: (path: string) => Promise<WorkspaceEntry[]>,
  {
    maxFiles = 4000,
    maxDepth = 8,
    concurrency = 8,
  }: { maxFiles?: number; maxDepth?: number; concurrency?: number } = {},
): Promise<string[]> {
  const files: string[] = [];
  let level = [''];
  for (let depth = 0; depth <= maxDepth && level.length > 0 && files.length < maxFiles; depth++) {
    const next: string[] = [];
    // A few folders at a time: a wide tree does not flood the workspace server.
    const listed: WorkspaceEntry[][] = [];
    for (let start = 0; start < level.length && files.length < maxFiles; start += concurrency)
      listed.push(
        ...(await Promise.all(
          level
            .slice(start, start + concurrency)
            .map((directory) => listDirectory(directory).catch(() => [] as WorkspaceEntry[])),
        )),
      );
    for (const entries of listed)
      for (const entry of entries) {
        if (entry.isDirectory) {
          if (!SKIPPED.has(entry.name)) next.push(entry.path);
        } else if (files.length < maxFiles) files.push(entry.path);
      }
    level = next;
  }
  return files;
}

/** Dispatched to open a workspace file in the side panel's Files tab (`detail`: its path). */
export const OPEN_WORKSPACE_FILE_EVENT = 'crowdex:open-workspace-file';

export function openWorkspaceFileInPanel(path: string): void {
  window.dispatchEvent(new CustomEvent(OPEN_WORKSPACE_FILE_EVENT, { detail: path }));
}
